import { useEffect, useRef, useState } from 'react';

/**
 * Timing for the animated splash "bridge" that continues the native splash
 * screen while auth resolves. All durations are milliseconds.
 */

/** Bloom: the candle catching. Ease-out-quart settle, no spin, no bounce. */
export const BLOOM_MS = 800;

/** Lift: lockup rises and fades as the overlay clears. */
export const LIFT_MS = 400;

/** Reduced-motion replacement for the lift — a plain crossfade. */
export const CROSSFADE_MS = 250;

/**
 * Floor on visible time. Below this the splash reads as a flash of noise on a
 * fast boot rather than an intentional hand-off.
 *
 * MUST stay comfortably above BLOOM_MS. At 700ms against a 600ms bloom the glow
 * reached full strength and began lifting 100ms later, so on a fast boot the
 * animation was over before it registered. The floor now leaves ~600ms of hold
 * after the bloom settles, so the pulse is actually seen.
 *
 * Only binding on fast/warm boots — a real cold boot waits on fonts and auth
 * anyway. Total to first paint stays inside the <2s cold-start budget in
 * CLAUDE.md: 800 bloom + 600 hold + 400 lift = 1.8s worst case.
 */
export const MIN_DISPLAY_MS = 1400;

/**
 * Ceiling on visible time. A hung auth call must never trap the user on the
 * splash; past this the overlay clears and the caller falls through to its own
 * loading state.
 */
export const MAX_DISPLAY_MS = 8000;

/**
 * `bloom` -> `hold` -> `lift` -> `done`.
 *
 * - `bloom`: the glow blooms in and the lockup breathes once.
 * - `hold`: the glow pulses slowly while the caller's work resolves.
 * - `lift`: the lockup rises and fades out.
 * - `done`: terminal. `onFinish` has fired; the caller should unmount.
 */
export type SplashPhase = 'bloom' | 'hold' | 'lift' | 'done';

interface UseSplashSequenceOptions {
  /** True while the caller still needs the splash held (fonts/auth pending). */
  visible: boolean;
  /** Skips the bloom and shortens the exit to a plain crossfade. */
  reducedMotion?: boolean;
  /** Called once, after the lift completes. */
  onFinish?: () => void;
}

interface UseSplashSequenceResult {
  phase: SplashPhase;
}

/**
 * Owns the splash timeline: phase transitions, the minimum-display floor, and
 * the maximum-display cap.
 *
 * Elapsed time is tracked with timers rather than clock arithmetic so the
 * sequence is deterministic under fake timers and immune to clock changes.
 */
export function useSplashSequence({
  visible,
  reducedMotion = false,
  onFinish,
}: UseSplashSequenceOptions): UseSplashSequenceResult {
  // Each flag latches once its timer fires; the phase is derived from them, so
  // no effect ever sets state synchronously.
  const [bloomSettled, setBloomSettled] = useState(false);
  const [minElapsed, setMinElapsed] = useState(false);
  const [capped, setCapped] = useState(false);
  const [liftDone, setLiftDone] = useState(false);

  // Held in a ref so a caller passing an inline arrow doesn't restart the exit.
  const onFinishRef = useRef(onFinish);
  useEffect(() => {
    onFinishRef.current = onFinish;
  }, [onFinish]);

  // Floor and ceiling both run from mount and never restart.
  useEffect(() => {
    const minTimer = setTimeout(() => setMinElapsed(true), MIN_DISPLAY_MS);
    const capTimer = setTimeout(() => setCapped(true), MAX_DISPLAY_MS);
    return () => {
      clearTimeout(minTimer);
      clearTimeout(capTimer);
    };
  }, []);

  // Bloom settles into the hold. Reduced motion has no bloom to settle.
  useEffect(() => {
    if (reducedMotion) return;
    const timer = setTimeout(() => setBloomSettled(true), BLOOM_MS);
    return () => clearTimeout(timer);
  }, [reducedMotion]);

  // Exit when the caller is done and the floor has passed, or at the cap.
  const shouldLift = capped || (!visible && minElapsed);

  // Lift completes -> terminal state, exactly one onFinish.
  useEffect(() => {
    if (!shouldLift || liftDone) return;
    const timer = setTimeout(
      () => {
        setLiftDone(true);
        onFinishRef.current?.();
      },
      reducedMotion ? CROSSFADE_MS : LIFT_MS,
    );
    return () => clearTimeout(timer);
  }, [shouldLift, liftDone, reducedMotion]);

  const phase: SplashPhase = liftDone
    ? 'done'
    : shouldLift
      ? 'lift'
      : reducedMotion || bloomSettled
        ? 'hold'
        : 'bloom';

  return { phase };
}
